/** What the assistant can do — shown for capabilities queries & prompt tiles. */

import { format, addDays } from "date-fns";
import { isAdmin, roleCanWrite } from "assistant/userRole";

function defaultSickLeaveExample(language) {
  const start = format(new Date(), "yyyy-MM-dd");
  const end = format(addDays(new Date(), 2), "yyyy-MM-dd");
  if (language === "en") {
    return {
      start,
      end,
      message: `Apply sick leave for Cherif from ${start} to ${end}`,
      label: "Apply sick leave — Cherif",
    };
  }
  return {
    start,
    end,
    message: `Appliquer un congé maladie pour Cherif du ${start} au ${end}`,
    label: "Congé maladie — Cherif",
  };
}

/** Write-command examples merged into the prompt chip row (preview → confirm). */
export function getCommandActionTiles(language, roleBucket) {
  if (!roleCanWrite(roleBucket)) return [];

  const sick = defaultSickLeaveExample(language);

  if (language === "en") {
    return [
      {
        id: "cmd-leave-cherif",
        icon: "edit_calendar",
        label: sick.label,
        message: sick.message,
        description: "Sick leave · preview then confirm",
        variant: "command",
      },
      {
        id: "cmd-assign-cherif",
        icon: "assignment_ind",
        label: "Assign Cherif Ousmane",
        message: "Assign Cherif Ousmane to Awine Gassar",
        description: "Assign teacher to establishment · preview then confirm",
        variant: "command",
      },
      {
        id: "cmd-activate-license",
        icon: "verified_user",
        label: "Activate license 205803",
        message: "Activate license for 205803",
        description: "Activate teacher license · preview then confirm",
        variant: "command",
      },
      {
        id: "cmd-deactivate-license",
        icon: "person_off",
        label: "Deactivate Cherif Ousmane",
        message: "Deactivate license for Cherif Ousmane",
        description: "Deactivate teacher license · preview then confirm",
        variant: "command",
      },
    ];
  }

  return [
    {
      id: "cmd-leave-cherif",
      icon: "edit_calendar",
      label: sick.label,
      message: sick.message,
      description: "Congé maladie · aperçu puis confirmation",
      variant: "command",
    },
    {
      id: "cmd-assign-cherif",
      icon: "assignment_ind",
      label: "Affecter Cherif Ousmane",
      message: "Affecter Cherif Ousmane à Awine Gassar",
      description: "Affecter une enseignante · aperçu puis confirmation",
      variant: "command",
    },
    {
      id: "cmd-activate-license",
      icon: "verified_user",
      label: "Activer licence 205803",
      message: "Activer la licence pour 205803",
      description: "Activer une licence · aperçu puis confirmation",
      variant: "command",
    },
    {
      id: "cmd-deactivate-license",
      icon: "person_off",
      label: "Désactiver Cherif Ousmane",
      message: "Désactiver la licence pour Cherif Ousmane",
      description: "Désactiver une licence · aperçu puis confirmation",
      variant: "command",
    },
  ];
}

export function getAssistantCapabilities(language, roleBucket) {
  const adminRole = isAdmin(roleBucket);
  const canWrite = roleCanWrite(roleBucket);

  if (language === "en") {
    const items = [
      {
        id: "cap-nav",
        icon: "explore",
        title: "Navigate the portal",
        description: "Jump to any section: teachers, assignments, leave, stats…",
        example: "Open leave requests",
        message: "open leave requests",
      },
      {
        id: "cap-page-help",
        icon: "help_outline",
        title: "Help on current page",
        description: "Explains what you see and how to use this screen.",
        example: "Help on this page",
        message: "help on this page",
      },
      {
        id: "cap-summary",
        icon: "dashboard",
        title: "Live portal summary",
        description: "KPI cards: teachers, pending leave, recruiters, assignments.",
        example: "Portal summary",
        message: "portal summary",
      },
      {
        id: "cap-leave",
        icon: "event_busy",
        title: "Leave requests",
        description: "Count pending/approved and link to the leave page.",
        example: "Pending leave",
        message: "pending leave",
      },
      {
        id: "cap-search",
        icon: "person_search",
        title: "Find a teacher",
        description: "Search by name or matricule — opens profile on single match.",
        example: "Find a teacher",
        message: "Help me find a teacher",
      },
      {
        id: "cap-write",
        icon: "assignment",
        title: "Assignment guide",
        description: "Step-by-step wizard for creating affectations.",
        example: "Assignment guide",
        message: "assignment guide",
      },
      {
        id: "cap-write-leave",
        icon: "edit_calendar",
        title: "Leave guide",
        description: "Walkthrough for submitting a leave request.",
        example: "Leave guide",
        message: "leave guide",
      },
      {
        id: "cap-write-license",
        icon: "verified_user",
        title: "License quota",
        description: "See licensed vs remaining slots.",
        example: "License quota",
        message: "license quota",
      },
      {
        id: "cap-area-attendance",
        icon: "map",
        title: "Area attendance",
        description: "Live zone summary: checked vs assigned teachers.",
        example: "Area attendance summary",
        message: "area attendance summary",
      },
      {
        id: "cap-estab-attendance",
        icon: "fact_check",
        title: "Establishment attendance",
        description: "Per-teacher report + lowest-attendance highlights (30 days).",
        example: "Establishment attendance report",
        message: "establishment attendance report",
      },
      {
        id: "cap-wizard-teacher",
        icon: "person_add",
        title: "Add teacher guide",
        description: "Steps to register a new teacher.",
        example: "Teacher guide",
        message: "teacher guide",
      },
      {
        id: "cap-wizard-att",
        icon: "school",
        title: "Attendance report guide",
        description: "How to generate establishment attendance reports.",
        example: "Attendance guide",
        message: "attendance guide",
      },
      {
        id: "cap-alerts",
        icon: "notifications_active",
        title: "Proactive alerts",
        description: "Surfaces pending leave, roster gaps, and key counts.",
        example: "What needs attention?",
        message: "what needs attention",
      },
      {
        id: "cap-faq",
        icon: "quiz",
        title: "FAQ answers",
        description: "Password, language, attendance, user accounts…",
        example: "How to change password?",
        message: "how to change password",
      },
      {
        id: "cap-establishments",
        icon: "business",
        title: "Establishment counts",
        description: "Total establishments in the directory.",
        example: "How many establishments?",
        message: "how many establishments",
      },
    ];

    if (adminRole) {
      items.push({
        id: "cap-users",
        icon: "admin_panel_settings",
        title: "User management help",
        description: "How to create portal accounts (admin only).",
        example: "How to create a user?",
        message: "how to create a user",
      });
    }

    if (!canWrite) {
      return items.filter(
        (i) =>
          i.id === "cap-wizard-att" ||
          (!i.id.includes("wizard") && !i.id.startsWith("cap-write"))
      );
    }

    return items;
  }

  // French
  const items = [
    {
      id: "cap-nav",
      icon: "explore",
      title: "Navigation portail",
      description: "Accéder à enseignantes, affectations, congés, statistiques…",
      example: "Ouvrir demandes de congé",
      message: "ouvrir demandes de congé",
    },
    {
      id: "cap-page-help",
      icon: "help_outline",
      title: "Aide page actuelle",
      description: "Explique l'écran où vous êtes et comment l'utiliser.",
      example: "Aide sur cette page",
      message: "aide sur cette page",
    },
    {
      id: "cap-summary",
      icon: "dashboard",
      title: "Résumé en direct",
      description: "KPI : enseignantes, congés en attente, recruteurs, affectations.",
      example: "Résumé du portail",
      message: "résumé du portail",
    },
    {
      id: "cap-leave",
      icon: "event_busy",
      title: "Demandes de congé",
      description: "Compte les en attente/approuvées et ouvre la page congés.",
      example: "Congés en attente",
      message: "congés en attente",
    },
    {
      id: "cap-search",
      icon: "person_search",
      title: "Trouver une enseignante",
      description: "Recherche par nom ou matricule — fiche si un seul résultat.",
      example: "Trouver une enseignante",
      message: "Aide-moi à trouver une enseignante",
    },
    {
      id: "cap-write",
      icon: "assignment",
      title: "Guide affectation",
      description: "Assistant pas à pas pour créer une affectation.",
      example: "Guide affectation",
      message: "guide affectation",
    },
    {
      id: "cap-write-leave",
      icon: "edit_calendar",
      title: "Guide congé",
      description: "Étapes pour soumettre une demande de congé.",
      example: "Guide congé",
      message: "guide congé",
    },
    {
      id: "cap-write-license",
      icon: "verified_user",
      title: "Quota licences",
      description: "Licenciés vs créneaux restants.",
      example: "Quota licences",
      message: "quota licences",
    },
    {
      id: "cap-area-attendance",
      icon: "map",
      title: "Synthèse zone",
      description: "Présence agrégée par province/département (données live).",
      example: "Synthèse présence zone",
      message: "synthèse présence zone",
    },
    {
      id: "cap-estab-attendance",
      icon: "fact_check",
      title: "Rapport établissement",
      description: "Présence par enseignant + points faibles sur 30 jours.",
      example: "Rapport présence établissement",
      message: "rapport présence établissement",
    },
    {
      id: "cap-wizard-teacher",
      icon: "person_add",
      title: "Guide enseignante",
      description: "Comment enregistrer une nouvelle enseignante.",
      example: "Guide enseignant",
      message: "guide enseignant",
    },
    {
      id: "cap-wizard-att",
      icon: "fact_check",
      title: "Guide rapport présence",
      description: "Générer un rapport de présence par établissement.",
      example: "Guide présence",
      message: "guide présence",
    },
    {
      id: "cap-alerts",
      icon: "notifications_active",
      title: "Alertes proactives",
      description: "Congés en attente, effectifs, écarts à surveiller.",
      example: "Qu'est-ce qui nécessite attention ?",
      message: "alertes",
    },
    {
      id: "cap-faq",
      icon: "quiz",
      title: "FAQ intégrée",
      description: "Mot de passe, langue, présence, comptes utilisateurs…",
      example: "Changer mot de passe ?",
      message: "changer mot de passe",
    },
    {
      id: "cap-establishments",
      icon: "business",
      title: "Établissements",
      description: "Nombre total d'établissements enregistrés.",
      example: "Combien d'établissements ?",
      message: "combien établissements",
    },
  ];

  if (adminRole) {
    items.push({
      id: "cap-users",
      icon: "admin_panel_settings",
      title: "Gestion utilisateurs",
      description: "Créer des comptes portail (admin uniquement).",
      example: "Créer un utilisateur ?",
      message: "créer un utilisateur",
    });
  }

  if (!canWrite) {
    return items.filter(
      (i) =>
        i.id === "cap-wizard-att" ||
        (!i.id.includes("wizard") && !i.id.startsWith("cap-write"))
    );
  }

  return items;
}

export function getQuickPromptTiles(language, roleBucket) {
  const caps = getAssistantCapabilities(language, roleBucket);
  const commandTiles = getCommandActionTiles(language, roleBucket);
  const lead = {
    id: "cap-what",
    icon: "auto_awesome",
    title: language === "en" ? "What can you do?" : "Que peux-tu faire ?",
    description:
      language === "en"
        ? "Full list of assistant capabilities"
        : "Liste complète des capacités de l'assistant",
    message: language === "en" ? "what can you do for me" : "que peux-tu faire pour moi",
    variant: "default",
  };
  const pageHelp = caps.find((c) => c.id === "cap-page-help");
  const skipIds = new Set(["cap-write", "cap-write-leave", "cap-page-help"]);
  const priorityIds = [
    "cap-search",
    "cap-summary",
    "cap-leave",
    "cap-write-license",
    "cap-area-attendance",
  ];
  const priority = priorityIds.map((id) => caps.find((c) => c.id === id)).filter(Boolean);
  const rest = caps.filter(
    (c) => c.id !== "cap-page-help" && !priorityIds.includes(c.id) && !skipIds.has(c.id) && c.id !== "cap-what"
  );

  const toTile = (cap, variant = "default") => ({
    id: cap.id,
    label: cap.title || cap.label,
    message: cap.message,
    icon: cap.icon,
    description: cap.description,
    variant,
  });

  return {
    promptTiles: [
      lead,
      ...commandTiles,
      ...priority.map((c) => toTile(c)),
      pageHelp ? toTile(pageHelp) : null,
      ...rest.slice(0, 2).map((c) => toTile(c)),
    ].filter(Boolean),
  };
}
